import { DOCUMENT } from "@angular/common";
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
  signal,
  ViewChild,
} from "@angular/core";
import { CreditConfigType } from "@bitwarden/web-vault/app/models/types/environement-config.type";

const CREDIT_CONFIG = process.env.CREDIT as unknown as CreditConfigType;
const COUNTDOWN_SECONDS = 30;
// How long to wait for AdSense to report a fill status before treating it as a failure.
const LOAD_TIMEOUT_SECONDS = 15;

@Component({
  selector: "app-desktop-ad-modal",
  templateUrl: "./desktop-ad-modal.component.html",
})
export class DesktopAdModalComponent implements AfterViewInit, OnDestroy {
  @Input() rewardAmount = 10;

  @Output() rewarded = new EventEmitter<void>();
  @Output() dismissed = new EventEmitter<void>();
  @Output() failedToLoad = new EventEmitter<void>();

  @ViewChild("adSlotEl") adSlotEl!: ElementRef<HTMLElement>;

  protected countdown = signal(COUNTDOWN_SECONDS);
  protected rewardGranted = signal(false);
  protected readonly publisherId = CREDIT_CONFIG.adsensePublisherId ?? "";
  protected readonly adSlot = CREDIT_CONFIG.desktopAdSlotId ?? "";

  private timer: ReturnType<typeof setInterval> | null = null;
  private loadTimer: ReturnType<typeof setTimeout> | null = null;
  private adObserver: MutationObserver | null = null;
  // Becomes true once the ad has either started counting down or failed to load,
  // so the load watchdog and the status observer can't both act on the same modal.
  private settled = false;
  private readonly document = inject(DOCUMENT);

  ngAfterViewInit(): void {
    // No publisher id means ads aren't configured for this environment.
    if (!this.publisherId) {
      this.failAdLoad();
      return;
    }
    this.loadAdSenseScript();
    // Observe the slot before requesting the ad so we don't miss the status change.
    this.observeAdStatus();
    this.startLoadWatchdog();
    this.requestAd();
  }

  private loadAdSenseScript(): void {
    if (this.document.querySelector(`script[src*="${this.publisherId}"]`)) {
      return;
    }
    const script = this.document.createElement("script");
    script.async = true;
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${this.publisherId}`;
    script.crossOrigin = "anonymous";
    this.document.head.appendChild(script);
  }

  private requestAd(): void {
    try {
      const w = this.document.defaultView as any;
      w.adsbygoogle = w.adsbygoogle || [];
      w.adsbygoogle.push({});
    } catch {
      // adsbygoogle.push can throw (script blocked, duplicate push, no slot) — without
      // this guard the countdown would never start and the modal would hang.
      this.failAdLoad();
    }
  }

  private observeAdStatus(): void {
    const ins = this.adSlotEl?.nativeElement;
    if (!ins) {
      return;
    }
    this.adObserver = new MutationObserver(() => {
      const status = ins.getAttribute("data-ad-status");
      if (status === "filled") {
        // Only start the clock once we know a real ad rendered.
        this.startCountdown();
      } else if (status === "unfilled") {
        this.failAdLoad();
      }
    });
    this.adObserver.observe(ins, { attributes: true, attributeFilter: ["data-ad-status"] });
  }

  private startLoadWatchdog(): void {
    this.loadTimer = setTimeout(() => {
      // AdSense never reported a fill status (slow, blocked, or never rendered).
      this.failAdLoad();
    }, LOAD_TIMEOUT_SECONDS * 1000);
  }

  private startCountdown(): void {
    if (!this.settle()) {
      return;
    }
    this.timer = setInterval(() => {
      const remaining = this.countdown() - 1;
      this.countdown.set(remaining);
      if (remaining <= 0) {
        clearInterval(this.timer!);
        this.timer = null;
        this.rewardGranted.set(true);
        this.rewarded.emit();
      }
    }, 1000);
  }

  private failAdLoad(): void {
    if (!this.settle()) {
      return;
    }
    this.failedToLoad.emit();
  }

  // Resolve the modal's pending state exactly once (ad started or failed), tearing down
  // the watchdog and observer. Returns false if already resolved so callers can bail.
  private settle(): boolean {
    if (this.settled) {
      return false;
    }
    this.settled = true;
    this.clearLoadWatchdog();
    this.adObserver?.disconnect();
    this.adObserver = null;
    return true;
  }

  private clearLoadWatchdog(): void {
    if (this.loadTimer) {
      clearTimeout(this.loadTimer);
      this.loadTimer = null;
    }
  }

  protected close(): void {
    this.dismissed.emit();
  }

  ngOnDestroy(): void {
    if (this.timer) {
      clearInterval(this.timer);
    }
    this.clearLoadWatchdog();
    this.adObserver?.disconnect();
  }
}
